/**
 * TealerLab — Product card variant switcher (global)
 * S'attache à TOUTES les cards [data-tlr-pcard] du document.
 * - Click sur un variant pill → met à jour le prix, le compare, le discount badge, et le data-add-variant du bouton ATC.
 * - Le bouton ATC est géré par tlr-fly-to-cart.js (delegation globale).
 * Compatible : home grid, collection, PDP variety.
 */
(function () {
  'use strict';

  function initCard(card) {
    if (card._tlrPcardBound) return;
    card._tlrPcardBound = true;

    const pills = card.querySelectorAll('[data-tlr-variant]');
    if (pills.length === 0) return;

    const priceEl = card.querySelector('[data-tlr-price]');
    const compareEl = card.querySelector('[data-tlr-compare]');
    const discountEl = card.querySelector('[data-tlr-discount]');
    const discountVal = card.querySelector('[data-tlr-discount-val]');
    const perGramEl = card.querySelector('[data-tlr-per-gram]');
    const atcBtn = card.querySelector('.tlr-pcard__atc');

    function fmtMoney(cents) {
      try {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: window.tlr?.currency || 'EUR' }).format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(2) + ' €';
      }
    }

    pills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        if (pill.classList.contains('is-soldout')) return;
        const wasActive = pill.classList.contains('is-active');

        // Active state
        pills.forEach((p) => {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');

        // Price update
        const newPrice = pill.getAttribute('data-price');
        const newCompare = pill.getAttribute('data-compare-price');
        const hasCompare = pill.getAttribute('data-has-compare') === '1';
        const discountPct = parseInt(pill.getAttribute('data-discount-pct') || '0', 10);
        const variantId = pill.getAttribute('data-variant-id');

        if (priceEl && newPrice) priceEl.textContent = newPrice;
        if (compareEl) {
          if (hasCompare && newCompare) {
            compareEl.textContent = newCompare;
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
        if (discountEl && discountVal) {
          if (discountPct > 0) {
            discountVal.textContent = discountPct;
            discountEl.removeAttribute('hidden');
          } else {
            discountEl.setAttribute('hidden', '');
          }
        }
        if (atcBtn && variantId) {
          atcBtn.setAttribute('data-add-variant', variantId);
        }
        // Update du prix à la mesure de la variante active. Le suffixe vient de
        // la pill : le gramme et l'unité ne se déduisent pas d'un montant.
        if (perGramEl) {
          const pgc = parseInt(pill.getAttribute('data-per-measure-cents') || '0', 10);
          if (pgc > 0) {
            perGramEl.textContent = fmtMoney(pgc) + (pill.getAttribute('data-per-measure-suffix') || '/gr');
          } else {
            perGramEl.textContent = '';
          }
        }
        const productId = productIdOf(card);
        // Shopper changes only: the pack builder replays a restored pack with pill.click().
        if (!wasActive && variantId && productId && (e.isTrusted || selectDriven)) {
          const optionEl = pill.closest('[data-tlr-option]');
          window.tlr?.events?.publish?.('tlr_variant', {
            product: productId,
            variant: variantId,
            option: optionEl ? optionEl.getAttribute('data-tlr-option') : '',
          });
        }
      });
    });
  }

  let selectDriven = false;

  // Every card carries its product id (data-tlr-products); pack cards also data-product-id.
  function productIdOf(card) {
    if (!card) return '';
    return card.getAttribute('data-tlr-products') || card.getAttribute('data-product-id') || '';
  }

  function bindAll() {
    document.querySelectorAll('[data-tlr-pcard]').forEach(initCard);
  }

  // Init au load + observer pour les cards ajoutées dynamiquement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  /* TLR perf: MutationObserver scopé aux nouvelles nodes uniquement.
     L'ancien pattern (bindAll sur chaque mutation) relançait querySelectorAll global
     sur tout le document à chaque insertion DOM = long task potentielle sur collection 300 produits.
     Nouveau : on parcourt uniquement les addedNodes et leurs enfants [data-tlr-pcard]. */
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        m.addedNodes.forEach(function(node) {
          if (!(node instanceof Element)) return;
          if (node.matches('[data-tlr-pcard]')) {
            initCard(node);
          } else {
            node.querySelectorAll('[data-tlr-pcard]').forEach(initCard);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Le fly-to-cart anime l'image automatiquement (cart:updated event)
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.tlr-pcard__atc[data-add-variant]');
    if (!btn || btn.disabled) return;
    const id = btn.getAttribute('data-add-variant');
    if (!id) return;

    e.preventDefault();
    btn.disabled = true;
    // innerHTML, not textContent: the button holds two responsive label spans
    const prevHTML = btn.innerHTML;
    btn.textContent = '...';
    // Error feedback stays longer than the success restore, so it is readable
    let restoreDelay = 600;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1, properties: { _tlr: 'v2', _tlr_entry: window.tlr?.events?.entryFor?.(id, 'card') } }] }),
      });
      if (res.ok) {
        window.tlr?.events?.added?.({ entry: 'card', variant: id, qty: 1, product: productIdOf(btn.closest('[data-tlr-pcard]')) });
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        console.warn('[tlr-pcard] add to cart failed', res.status);
        btn.textContent = btn.dataset.errorLabel || 'Erreur — réessaie';
        restoreDelay = 1800;
      }
    } catch (err) {
      console.warn('[tlr-pcard] add to cart failed', err);
      btn.textContent = btn.dataset.errorLabel || 'Erreur — réessaie';
      restoreDelay = 1800;
    }

    setTimeout(() => {
      btn.disabled = false;
      btn.innerHTML = prevHTML;
    }, restoreDelay);
  });

  // le change est renvoyé sur la pill correspondante — un seul chemin de switch.
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-tlr-variant-select]');
    if (!sel) return;
    const card = sel.closest('[data-tlr-pcard]');
    const pill = card && card.querySelector('[data-tlr-variant][data-variant-id="' + sel.value + '"]');
    if (!pill) return;
    selectDriven = true;
    try { pill.click(); } finally { selectDriven = false; }
  });
})();
